import React, { createContext, useState, useEffect } from "react";
import axios from "axios";
import jwt_decode from "jwt-decode";

import { GetBaseURL } from "../components/utils/urlUtils";

export interface CaseData {
  caseId: string; // Assuming caseId is a string
  // other case fields...
}

export interface InvoiceData {
  invoiceId: string; // Assuming invoiceId is a string
  // other invoice fields...
}

export interface NoteData {
  noteId: string; // Assuming noteId is a string
  // other note fields...
}

interface DecodedToken {
  userId: string;
  firmId: string;
  // Add other token properties as per your token structure
}

export interface ClientInfoData {
  cases: CaseData[];
  invoices: InvoiceData[];
  notes: NoteData[];
  // Add other fields as necessary
}

export interface ClientData {
  personalDetails: {
    name: string;
    email: string;
    phone: string;
    address: string;
    // ... other personal details ...
  };
  info: ClientInfoData; // Using ClientInfoData here
  // ... any other fields specific to the client ...
}

export interface FirmStripeData {
  customerId: string | null;
  subscriptionId: string | null;
  currentPlan: string | null;
  subscriptionDuration: string | null;
  isTrialActive: boolean;
  trialExpiration: Date | null;
  selectedPlan?: string | null;
  hasCancelled?: boolean;
}

export interface FirmData {
  _id: string;
  name: string;
  slug?: string;
  address:
    | string
    | {
        line1?: string | null;
        line2?: string | null;
        city?: string | null;
        region?: string | null;
        postal?: string | null;
        country?: string | null;
      };
  country?: string;
  currency?: string;
  stripe: FirmStripeData;
  connectedStripeAccountId: string | null;
  isStripeConnected: boolean;
  users: string[];
  subscriptionStatus: "free trial" | "basic" | "pro" | "elite";
  email: string;
  nextBillDate?: Date | null;
  adminUserId: string;
  clients: ClientData[];
  isOnTrial: boolean;
  branding?: {
    logoUrl: string | null;
    primaryColor: string | null;
    accentColor: string | null;
  };
  jurisdiction?: {
    country: string;
    region: string;
  };
  practiceAreas?: string[];
  firmType?: "solicitor" | "transactional" | "litigation" | "full_service";
  primaryPhone?: string | null;
  primaryEmail?: string | null;
  settings?: {
    numbering?: {
      matterPrefix?: string;
      invoicePrefix?: string;
      receiptPrefix?: string;
    };
  };
}

export interface UserPermissions {
  canManageBilling?: boolean;
  canViewRevenue?: boolean;
  canManageClients?: boolean;
  canManageMatters?: boolean;
  canManageDocuments?: boolean;
  canManageCalendar?: boolean;
  canManageTeam?: boolean;
  canViewReports?: boolean;
  canManageSettings?: boolean;
}

export interface UserData {
  _id: string;
  email: string;
  firmId: string;
  name: string;
  title?: string | null;
  phone?: string | null;
  role?: string | null;
  signatureBlock?: string | null;
  permissions?: UserPermissions;
}

export type Terminology = Record<string, string>;

interface UserDataContextValue {
  userData: UserData;
  firmData: FirmData;
  terminology: Terminology;
  setUserData: React.Dispatch<React.SetStateAction<UserData>>;
  setFirmData: React.Dispatch<React.SetStateAction<FirmData>>;
  refreshUserData: () => Promise<void>;
  refreshFirmData: () => Promise<void>;
  canManageBilling: boolean;
}

const UserDataContext = createContext<UserDataContextValue | undefined>(
  undefined
);

export const useUserDataContext = () => {
  const context = React.useContext(UserDataContext);
  if (!context) {
    throw new Error(
      "useUserDataContext must be used within UserDataContextProvider"
    );
  }
  return context;
};

export const UserDataContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const initialUserDataState: UserData = {
    _id: "",
    email: "",
    firmId: "",
    name: "",
    title: null,
    phone: null,
    signatureBlock: null,
  };

  const initialFirmDataState: FirmData = {
    _id: "",
    name: "",
    address: "",
    stripe: {
      customerId: null,
      subscriptionId: null,
      currentPlan: null,
      subscriptionDuration: null,
      isTrialActive: true,
      trialExpiration: null,
      selectedPlan: null,
      hasCancelled: false,
    },
    connectedStripeAccountId: null,
    isStripeConnected: false,
    users: [],
    subscriptionStatus: "free trial",
    email: "",
    nextBillDate: null,
    adminUserId: "",
    clients: [],
    isOnTrial: false,
    branding: {
      logoUrl: null,
      primaryColor: null,
      accentColor: null,
    },
  };

  const [userData, setUserData] = useState<UserData>(initialUserDataState);
  const [firmData, setFirmData] = useState<FirmData>(initialFirmDataState);
  const [terminology, setTerminology] = useState<Terminology>({});

  // Fetch both user and firm from the single /api/auth/me endpoint
  const refreshSession = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    try {
      const response = await axios.get(`${GetBaseURL()}/api/auth/me`, {
        headers: { "x-auth-token": token },
      });

      const { user, firm } = response.data;
      if (user) {
        setUserData({
          _id: user.id,
          email: user.email,
          firmId: user.firmId,
          name: user.name,
          title: user.title || null,
          phone: user.phone || null,
          role: user.role || "staff",
          signatureBlock: user.signatureBlock || null,
          permissions: user.permissions || {},
        });
      }
      if (firm) {
        const isOnTrial =
          firm.stripe?.plan === "free trial" &&
          firm.stripe?.trialExpiration &&
          new Date(firm.stripe.trialExpiration) > new Date();

        setFirmData({
          ...firm,
          _id: firm._id,
          subscriptionStatus: firm.stripe?.plan || "free trial",
          isOnTrial: isOnTrial || false,
        });
      }
    } catch (error) {
      console.error("Error refreshing session:", error);
    }
  };

  const refreshUserData = async () => refreshSession();
  const refreshFirmData = async () => refreshSession();

  useEffect(() => {
    refreshSession();
  }, []);

  // ── Fetch terminology when firm is loaded ────────────────────────────
  useEffect(() => {
    if (!firmData?._id) return;
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    axios
      .get(`${GetBaseURL()}/api/firm/terminology`, {
        headers: { "x-auth-token": token },
      })
      .then((res) => setTerminology(res.data || {}))
      .catch(() => {});
  }, [firmData?._id]);

  // ── Auto-cleanup sample data when thresholds are met ────────────────
  useEffect(() => {
    if (!firmData?._id) return;
    const token = localStorage.getItem("accessToken");
    if (!token) return;
    // Fire-and-forget: check thresholds and auto-cleanup
    axios
      .get(`${GetBaseURL()}/api/firm/sample-data/status`, {
        headers: { "x-auth-token": token },
      })
      .catch(() => {}); // ignore errors
  }, [firmData?._id]);

  // ── Dynamic branding: inject firm colors as CSS variables ──────────
  // Custom branding is gated to free trial or elite tier.
  // ThemeProvider handles this; no duplicate injection needed here.

  // Firm admin or users with explicit billing permission can manage subscriptions
  const canManageBilling =
    (!!userData._id && !!firmData.adminUserId && userData._id === firmData.adminUserId) ||
    userData.permissions?.canManageBilling === true;

  const contextValue: UserDataContextValue = {
    userData,
    firmData,
    terminology,
    setUserData,
    setFirmData,
    refreshUserData,
    refreshFirmData,
    canManageBilling,
  };

  return (
    <UserDataContext.Provider
      value={{
        userData,
        firmData,
        terminology,
        setUserData,
        setFirmData,
        refreshUserData,
        refreshFirmData,
        canManageBilling,
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};

export default UserDataContextProvider;
